import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiFileText,
  FiVideo,
  FiBarChart2,
  FiLogOut,
  FiSettings,
  FiUsers,
  FiCheckCircle,
} from 'react-icons/fi';
import { useEffect } from 'react';
import GripLogo from '@/assets/grip-logo.png';
import GripWallpaper from '@/assets/grip-wallpaper-nw.jpg';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: clamp(1rem, 3vw, 2rem);
  overflow: hidden;
  background-image: url(${GripWallpaper});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  position: relative;
  
  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: clamp(0.75rem, 2vw, 1.5rem);
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: clamp(0.75rem, 2vw, 1.25rem) clamp(1rem, 3vw, 2rem);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const BrandSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  height: 50px;
`;

const Logo = styled.img`
  height: clamp(95px, 7vw, 70px);
  width: 160px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const IconButton = styled(motion.button)<{ $isDark: boolean }>`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isDark }) => $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid ${({ $isDark }) => $isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  color: ${({ $isDark }) => $isDark ? '#ffffff' : '#0f172a'};

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${({ $isDark }) => $isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 1)'};
    border-color: #ff6b35;
    color: #ff6b35;
    box-shadow: 0 6px 16px rgba(255, 107, 53, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }
`;


const MainContent = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  // gap: clamp(1rem, 2vw, 1rem);
  overflow: hidden;
  align-items: start;
  height: 100%;
  
  @media (max-width: 1024px) {
    grid-template-columns: 35% 30% 35%;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    overflow-y: auto;
  }
`;

const NavigationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.75rem, 2vw, 1.5rem);
  height: 100%;
  justify-content: center;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.muted};
    }
  }
  
  @media (max-width: 768px) {
    height: auto;
  }
`;

const NavButton = styled(motion.button)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: clamp(0.75rem, 2vw, 1.5rem);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1.5vw, 1rem);
  text-align: left;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    background: ${({ theme }) => 
      theme.colors.background === '#0f172a' 
        ? 'linear-gradient(135deg, rgba(51, 65, 85, 0.95) 0%, rgba(30, 41, 59, 0.95) 100())'
        : 'rgba(255, 255, 255, 0.85)'};
  }

  &:active {
    transform: translateX(4px);
  }
  
  @media (max-width: 768px) {
    &:hover {
      transform: translateY(-4px);
    }
    &:active {
      transform: translateY(-2px);
    }
  }
`;

const NavButtonIcon = styled.div<{ color: string }>`
  width: clamp(40px, 6vw, 48px);
  height: clamp(40px, 6vw, 48px);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: ${({ color }) => `0 0 0 2px ${color}33, 0 8px 18px ${color}40`};

  svg {
    width: clamp(20px, 3vw, 24px);
    height: clamp(20px, 3vw, 24px);
    color: #ffffff;
  }
`;

const NavButtonText = styled.div`
  flex: 1;
`;

const NavButtonTitle = styled.h3`
  font-size: clamp(0.875rem, 2vw, 1.125rem);
  color: ${({ theme }) => theme.colors.onBackground};
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const MiddleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1.5vw, 1rem);
  height: 100%;
  justify-content: center;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing.sm};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.muted};
    }
  }
  
  @media (max-width: 768px) {
    height: auto;
  }
`;

export const HomePage = () => {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const { logout, user } = useAuth();

  useEffect(() => {
    // Fetch program updates from actions
  }, []);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        // Call logout API endpoint
        await api.post('/auth/logout');
      } catch (error) {
        // Continue with logout even if API call fails
        console.error('Logout API error:', error);
      } finally {
        // Clear local state and navigate
        logout();
        navigate('/login', { replace: true });
      }
    }
  };

  const navigationButtons = [
    {
      id: 'program-overview',
      title: 'Program Overview',
      icon: FiVideo,
      color: '#3b82f6',
      path: '/video-library',
    },
    {
      id: 'program-updates',
      title: 'What is happening',
      icon: FiBarChart2,
      color: '#ff6b35',
      path: '/program-updates',
    },
    {
      id: 'document-hub',
      title: 'Document Hub',
      icon: FiFileText,
      color: '#f7931e',
      path: '/documents',
    },
    {
      id: 'grip-team',
      title: 'GRIP Team',
      icon: FiUsers,
      color: '#3b82f6',
      path: '/teams',
    },
  ];

  return (
    <Container>
      <Header>
        <BrandSection>
          <Logo src={GripLogo} alt="GRIP Logo" onClick={() => navigate('/home')} />
        </BrandSection>

        <HeaderActions>
          <IconButton
            $isDark={mode === 'dark'}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            onClick={() => navigate('/settings')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Settings"
          >
            <FiSettings />
          </IconButton>

          <IconButton
            $isDark={mode === 'dark'}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            onClick={handleLogout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Logout"
          >
            <FiLogOut />
          </IconButton>
        </HeaderActions>
      </Header>

      <MainContent>
        {/* Left Column - 4 Navigation Buttons */}
        <NavigationSection>
          {navigationButtons.map((button, index) => (
            <NavButton
              key={button.id}
              onClick={() => navigate(button.path)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <NavButtonIcon color={button.color}>
                <button.icon />
              </NavButtonIcon>
              <NavButtonText>
                <NavButtonTitle>{button.title}</NavButtonTitle>
              </NavButtonText>
            </NavButton>
          ))}
        </NavigationSection>

        {/* Middle Column - Empty (40%) */}
        <MiddleSection />

        {/* Right Column - Program Updates from Actions */}
        <SummarySection>
          {user?.role === 'program_manager' && (
            <NavButton
              onClick={() => navigate('/actions')}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <NavButtonIcon color="#10b981">
                <FiCheckCircle />
              </NavButtonIcon>
              <NavButtonText>
                <NavButtonTitle>Program Director Dashboard</NavButtonTitle>
              </NavButtonText>
            </NavButton>
          )}
        </SummarySection>
      </MainContent>
    </Container>
  );
};
